import { useEffect, useState } from "react";

// Field conditions from Open-Meteo (free, no key). Agronomy-first outputs:
// spray windows, rain, growing degree days, and the weather during a visit.
// Everything degrades to "unavailable" offline; nothing here is required.

export type Units = "us" | "metric";
export interface Day { date: string; tMax: number; tMin: number; rainProb: number; rainSum: number; windMax: number; code: number }
export interface Hour { time: string; temp: number; rainProb: number; wind: number; gust: number }
export interface Forecast { days: Day[]; hours: Hour[]; units: Units; now: { temp: number; wind: number; code: number } }
export interface SprayWindow { start: string; end: string; hours: number }

const cache = new Map<string, { at: number; p: Promise<any> }>();
function cached<T>(key: string, ttl: number, fn: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttl) return hit.p;
  const p = fn().catch((e) => { cache.delete(key); throw e; });
  cache.set(key, { at: Date.now(), p });
  return p;
}
const q = (u: Units) => (u === "us" ? "&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch" : "&wind_speed_unit=kmh");
const round = (n: number, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

export function fetchForecast(lat: number, lng: number, units: Units): Promise<Forecast> {
  const key = `f:${round(lat)}:${round(lng)}:${units}`;
  return cached(key, 30 * 60_000, async () => {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&timezone=auto&forecast_days=7${q(units)}`
      + "&current=temperature_2m,wind_speed_10m,weather_code"
      + "&hourly=temperature_2m,precipitation_probability,wind_speed_10m,wind_gusts_10m"
      + "&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum,wind_speed_10m_max,weather_code";
    const r = await fetch(url);
    if (!r.ok) throw new Error(`weather ${r.status}`);
    const j = await r.json();
    const d = j.daily, h = j.hourly;
    return {
      units,
      now: { temp: j.current.temperature_2m, wind: j.current.wind_speed_10m, code: j.current.weather_code },
      days: d.time.map((t: string, i: number) => ({ date: t, tMax: d.temperature_2m_max[i], tMin: d.temperature_2m_min[i], rainProb: d.precipitation_probability_max[i] ?? 0, rainSum: d.precipitation_sum[i] ?? 0, windMax: d.wind_speed_10m_max[i], code: d.weather_code[i] })),
      hours: h.time.map((t: string, i: number) => ({ time: t, temp: h.temperature_2m[i], rainProb: h.precipitation_probability[i] ?? 0, wind: h.wind_speed_10m[i], gust: h.wind_gusts_10m[i] })),
    };
  });
}

// Spray window: consecutive daylight hours with wind 3–10 mph (5–16 km/h),
// gusts under 15 mph (24 km/h) and rain chance under 20%. Next 48h.
export function sprayWindows(f: Forecast): SprayWindow[] {
  const [lo, hi, gustMax] = f.units === "us" ? [3, 10, 15] : [5, 16, 24];
  const now = Date.now();
  const out: SprayWindow[] = [];
  let run: Hour[] = [];
  const flush = () => { if (run.length >= 2) out.push({ start: run[0].time, end: run[run.length - 1].time, hours: run.length }); run = []; };
  for (const h of f.hours) {
    const t = new Date(h.time).getTime();
    if (t < now - 3_600_000 || t > now + 48 * 3_600_000) continue;
    const hr = new Date(h.time).getHours();
    const ok = hr >= 6 && hr <= 19 && h.wind >= lo && h.wind <= hi && h.gust <= gustMax && h.rainProb < 20;
    if (ok) run.push(h); else flush();
  }
  flush();
  return out;
}

// Growing degree days (base 50°F / 10°C, capped 86°F / 30°C) since `start`.
export function fetchGdd(lat: number, lng: number, start: string, units: Units): Promise<{ gdd: number; rain: number; days: number }> {
  const end = new Date(Date.now() - 2 * 86_400_000).toISOString().slice(0, 10);
  const key = `g:${round(lat)}:${round(lng)}:${start}:${units}`;
  return cached(key, 6 * 3_600_000, async () => {
    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${start}&end_date=${end}&timezone=auto${q(units)}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum`;
    const r = await fetch(url);
    if (!r.ok) throw new Error(`gdd ${r.status}`);
    const d = (await r.json()).daily;
    const [base, cap] = units === "us" ? [50, 86] : [10, 30];
    let gdd = 0, rain = 0;
    d.time.forEach((_: string, i: number) => {
      const hi = Math.min(d.temperature_2m_max[i] ?? base, cap), lo = Math.max(d.temperature_2m_min[i] ?? base, base);
      gdd += Math.max(0, (hi + lo) / 2 - base);
      rain += d.precipitation_sum[i] ?? 0;
    });
    return { gdd: Math.round(gdd), rain: Math.round(rain * 100) / 100, days: d.time.length };
  });
}

// The weather during a visit (archive for past days, forecast API covers ~recent).
export function fetchVisitWeather(lat: number, lng: number, iso: string, units: Units): Promise<{ temp: number; wind: number; rain: number; code: number } | null> {
  const day = iso.slice(0, 10);
  const key = `v:${round(lat)}:${round(lng)}:${day}:${units}`;
  return cached(key, 6 * 3_600_000, async () => {
    const recent = Date.now() - new Date(day).getTime() < 5 * 86_400_000;
    const host = recent ? "https://api.open-meteo.com/v1/forecast" : "https://archive-api.open-meteo.com/v1/archive";
    const range = recent ? `&past_days=5&forecast_days=1` : `&start_date=${day}&end_date=${day}`;
    const r = await fetch(`${host}?latitude=${lat}&longitude=${lng}&timezone=auto${range}${q(units)}&hourly=temperature_2m,wind_speed_10m,precipitation,weather_code`);
    if (!r.ok) return null;
    const h = (await r.json()).hourly;
    const target = new Date(iso).getTime();
    let best = -1, diff = Infinity;
    h.time.forEach((t: string, i: number) => { const d = Math.abs(new Date(t).getTime() - target); if (d < diff) { diff = d; best = i; } });
    if (best < 0) return null;
    return { temp: h.temperature_2m[best], wind: h.wind_speed_10m[best], rain: h.precipitation[best], code: h.weather_code[best] };
  });
}

export function useAsync<T>(fn: (() => Promise<T>) | null, deps: unknown[]): { data: T | null; error: boolean } {
  const [s, set] = useState<{ data: T | null; error: boolean }>({ data: null, error: false });
  useEffect(() => {
    if (!fn) return;
    let alive = true;
    fn().then((data) => alive && set({ data, error: false })).catch(() => alive && set({ data: null, error: true }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return s;
}

// WMO weather codes → emoji + short label (emoji are category glyphs, README §7).
export function sky(code: number): { emoji: string; label: string } {
  if (code === 0) return { emoji: "☀️", label: "Clear" };
  if (code <= 2) return { emoji: "🌤️", label: "Partly cloudy" };
  if (code === 3) return { emoji: "☁️", label: "Overcast" };
  if (code <= 48) return { emoji: "🌫️", label: "Fog" };
  if (code <= 57) return { emoji: "🌦️", label: "Drizzle" };
  if (code <= 67) return { emoji: "🌧️", label: "Rain" };
  if (code <= 77) return { emoji: "🌨️", label: "Snow" };
  if (code <= 82) return { emoji: "🌧️", label: "Showers" };
  return { emoji: "⛈️", label: "Storms" };
}

export const tempU = (u: Units) => (u === "us" ? "°F" : "°C");
export const windU = (u: Units) => (u === "us" ? "mph" : "km/h");
export const rainU = (u: Units) => (u === "us" ? "in" : "mm");
